import { useEffect, useMemo, useState } from 'preact/hooks'
import { AccountLayoutContextProvider, useAccountLayoutContext } from './AccountProfileContext'
import Icon from '@/components/snippets/icon/icon'
import { AccountKidProfiles } from '@/components/libs/omneo/components/AccountKidsProfiles'
import { AccountWishlists } from '@/components/libs/omneo/components/AccountWishlists'
import { AccountProfile } from './AccountProfile'
import { AccountDashboard } from './AccountDashboard'
import { AccountOrders } from './AccountOrders'
import { AccountFitClubLoyalty } from './AccountFitClubLoyalty'
import { AccountAddress } from './AccountAddress'
import { AccountBadge } from './AccountBadge'
import { AccountAppointments } from './AccountAppointments'
import { AccountOffers } from './AccountOffers'
import { session } from '@/components/helpers/session'
import { Suspense } from './Suspense'
import { formatMoney } from '@shopify/theme-currency'
import { OmneoClient } from '../classes/client'
import { accountTranslations } from '../helpers/translations'
import LoaderSpin from '@/components/snippets/loader-spin/loader-spin'

const navigationItems = [
  {
    icon: 'bell',
    title: 'Dashboard',
    path: '/'
  },
  {
    icon: 'profile',
    title: accountTranslations.account_dashboard.navigation.profile,
    path: 'my-profile'
  },
  {
    icon: 'kid',
    title: accountTranslations.account_dashboard.navigation.kids_profiles,
    path: 'kids-profiles'
  },
  {
    icon: 'fit-club',
    title: accountTranslations.account_dashboard.navigation.rewards,
    path: 'fit-club'
  },
  // Note: Temporarily hidden. Some rework is required on this page.
  // {
  //   icon: 'gift-give',
  //   title: 'Members Offers',
  //   path: 'offers'
  // },
  {
    icon: 'package',
    title: accountTranslations.account_dashboard.navigation.orders,
    path: 'orders'
  },
  {
    icon: 'address',
    title: accountTranslations.account_dashboard.navigation.addresses,
    path: 'addresses'
  },
  {
    icon: 'calendar',
    title: accountTranslations.account_dashboard.navigation.fitting_appointments,
    path: 'bookings'
  },
  {
    icon: 'heart',
    title: accountTranslations.account_dashboard.navigation.wishlist,
    path: 'wishlist'
  }
]

const AccountBreadcrumbs = ({ handlePushState }) => {
  const { config } = useAccountLayoutContext()
  const page = navigationItems.find((item) => item.path === config.path)

  return (
    <div className="flex flex-row gap-2 paragraph-sm text-neutrals-secondary-grey">
      <span className={'cursor-pointer'} onClick={() => handlePushState('/')}>
        {accountTranslations.account_dashboard.navigation.breadcrumb_title}
      </span>
      <span>&#62;</span>
      <span>{page?.title}</span>
    </div>
  )
}
const NavigationBadge = ({ props }) => {
  const { cacheConfig } = useAccountLayoutContext()
  const handleViewRewards = () => props.handlePushState('fit-club')
  const balance = useMemo(() => {
    return cacheConfig?.cacheData?.Balance
  }, [cacheConfig?.cacheData?.Balance])

  return (
    <AccountBadge
      color="VIOLET"
      size={'full'}
      title={
        <div className={'flex max-w-96 flex-row justify-between gap-4 md:w-full md:max-w-full'}>
          <div>{accountTranslations.account_dashboard.rewards.title}: {formatMoney((balance?.reward_balance ?? 0) * 100)}</div>
          <div
            onClick={handleViewRewards}
            className={'paragraph-xs cursor-pointer font-normal underline underline-offset-1'}
          >
            {accountTranslations.account_dashboard.rewards.cta}
          </div>
        </div>
      }
    />
  )
}
const AccountLogout = () => {
  const [isLoading, setIsLoading] = useState(false)

  return (
    <button
      onClick={async () => {
        setIsLoading(true)

        // Logout Omneo
        await OmneoClient.logout()

        // Clear session tokens
        session.omneoAuthToken.set()
        session.omneoAuthExpiry.set()

        // Clear shopify customer session
        window.location.href = '/account/logout'
      }}
      className={`my-auto flex cursor-pointer flex-row items-center gap-4 pt-6 md-max:pb-6 text-black ${
        isLoading ? 'opacity-50' : ''
      }`}
      disabled={isLoading}
    >
      <Icon id={'exit'} />
      <div className="relative font-bold underline"><span className={isLoading ? 'invisible' : ''}>Logout</span>{isLoading && <LoaderSpin />}</div>
    </button>
  )
}

const AccountNavigationLinks = ({ props, handleIsOpen }) => {
  const baseUrl = '/account'
  const href = (path) => path !== '/' ? `${baseUrl}?section=${path}` : baseUrl
  return (
    <div className="flex flex-col gap-4">
      {navigationItems.map((item) => {
        const isSelectedTextColour = item.path === props.path ? 'text-primary-blue' : 'text-black'
        return (
          <a
            onClick={(e) => {
              e.preventDefault()
              props.handlePushState(item.path, false, true)
              if (handleIsOpen) {
                handleIsOpen()
              }
            }}
            href={href(item.path)}
            title={item.title}
            className={`my-auto flex cursor-pointer flex-row items-center gap-4 ${isSelectedTextColour}`}
          >
            <Icon id={item.icon} className={` ${isSelectedTextColour}`} />
            <div className="font-bold underline">{item.title}</div>
          </a>
        )
      })}
      <AccountLogout />
    </div>
  )
}

const ModalNavigation = ({ props }) => {
  const [isOpen, setIsOpen] = useState(false)
  const handleIsOpen = () => setIsOpen((o) => !o)

  return (
    <div className="">
      <div onClick={handleIsOpen} className="flex flex-row justify-between py-4 mt-2 cursor-pointer">
        <p className="text-lg font-bold">Menu</p>
        <p className="font-bold">{isOpen ? <Icon id="chevron-up" /> : <Icon id="chevron-down" />}</p>
      </div>
      {isOpen && <AccountNavigationLinks props={props} handleIsOpen={handleIsOpen} />}
    </div>
  )
}

const AccountPageBanner = () => {
  return (
    <div className="absolute inset-0 top-0 hidden w-full h-96 bg-primary-blue md:block">
      <img className="object-cover w-full h-full" src={accountGlobal?.page?.feature_banner} />
    </div>
  )
}

const AccountNavigationBar = () => {
  const { config, handlePushState } = useAccountLayoutContext()
  return (
    <div className="h-fit w-full md:sticky md:top-40 md:max-w-[21rem] md:pl-4">
      <div className="flex flex-col w-full h-full gap-0 p-6 pb-0 bg-neutrals-base md:gap-6 md:rounded-xl md:p-10">
        <div className="mb-2 md:mb-0">
          <AccountBreadcrumbs handlePushState={handlePushState} />
        </div>
        <h3 className={'mb-6 text-4xl font-bold leading-none md:mb-0 md:text-5xl'}>Fit Club Hub</h3>
        <div className={'py-1 perspective-0'}>
          <NavigationBadge props={{ ...config, handlePushState }} />
        </div>

        <HideOnMobile>
          <AccountNavigationLinks props={{ ...config, handlePushState }} />
        </HideOnMobile>
        <HideOnDesktop>
          <ModalNavigation props={{ ...config, handlePushState }} />
        </HideOnDesktop>
      </div>
    </div>
  )
}

const HideOnMobile = ({ children }) => <div className="hidden md:block">{children}</div>
const HideOnDesktop = ({ children }) => <div className="md:hidden">{children}</div>

const AccountBodyRender = ({ props }) => {
  const { config } = useAccountLayoutContext()

  switch (config.path) {
    case '/': {
      return <AccountDashboard />
    }
    case 'my-profile': {
      return <AccountProfile />
    }
    case 'kids-profiles': {
      return <AccountKidProfiles />
    }
    case 'fit-club': {
      return <AccountFitClubLoyalty />
    }
    case 'offers': {
      return <AccountOffers />
    }
    case 'bookings': {
      return <AccountAppointments />
    }
    case 'orders': {
      return <AccountOrders />
    }
    case 'addresses': {
      return <AccountAddress />
    }
    case 'wishlist': {
      return <AccountWishlists />
    }
    default: {
      return <div>404: {config.path} Not found.</div>
    }
  }
}

/**
 *
 * @returns
 */
const AccountBody = () => {
  return (
    <div className="flex-grow w-full overflow-hidden md:mt-64">
      <div className="mx-auto min-h-[650px] w-full px-8 py-10 md:px-18 md:py-8 2xl:px-32">
        <AccountBodyRender />
      </div>
    </div>
  )
}

/**
 *
 * @returns
 */
const AccountPageView = () => {
  const { cacheConfig } = useAccountLayoutContext()
  return (
    <div id="top">
      <AccountPageBanner />
      <div className="z-10 flex flex-col h-full pb-4 md:flex-row md:pt-40">
        <AccountNavigationBar />
        <Suspense
          loadingState={<></>}
          props={{
            asyncCallback: cacheConfig.getUserData
          }}
        >
          <AccountBody />
        </Suspense>
      </div>
    </div>
  )
}

export default function AccountPage() {
  return (
    <AccountLayoutContextProvider>
      <AccountPageView />
    </AccountLayoutContextProvider>
  )
}
